import { useCallback, useMemo } from 'react'
import { collection, query, where, doc, serverTimestamp } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { setDoc, deleteDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import type { TotemFollow } from '../types'
import { fbt } from 'fbtee'

export const totemFollowId = (userId: string, groupId: string, totemId: string) =>
  `${userId}_${groupId}_${totemId}`

/** Identifies a totem across group boundaries — a followed totem lives in a
 *  subcollection of a group the follower isn't in, so the pair is the handle. */
export interface TotemRef {
  groupId: string
  totemId: string
}

export const totemRefKey = (groupId: string, totemId: string) => `${groupId}_${totemId}`

export function useTotemFollows(userId: string | null) {
  const ref = userId
    ? (query(collection(db, 'totemFollows'), where('userId', '==', userId)) as Query<TotemFollow>)
    : null
  const { data: follows, loading, error } = useFirestoreCollection<TotemFollow>(ref)

  const followedRefs = useMemo<TotemRef[]>(
    () => follows.map(f => ({ groupId: f.groupId, totemId: f.totemId })),
    [follows],
  )

  const followedKeys = useMemo(
    () => new Set(follows.map(f => totemRefKey(f.groupId, f.totemId))),
    [follows],
  )

  const isFollowing = useCallback(
    (groupId: string, totemId: string) => followedKeys.has(totemRefKey(groupId, totemId)),
    [followedKeys],
  )

  const followTotem = useCallback(
    async (groupId: string, totemId: string, source: 'qr' | 'in_app') => {
      if (!userId) return
      await runMutation(fbt("Couldn't follow the totem", 'Error toast when following a totem fails'), async () => {
        await setDoc(doc(db, 'totemFollows', totemFollowId(userId, groupId, totemId)), {
          userId,
          groupId,
          totemId,
          createdAt: serverTimestamp(),
        })
        track('totem_followed', { group_id: groupId, entry_point: source })
      })
    },
    [userId],
  )

  const unfollowTotem = useCallback(
    async (groupId: string, totemId: string) => {
      if (!userId) return
      await runMutation(fbt("Couldn't unfollow the totem", 'Error toast when unfollowing a totem fails'), async () => {
        await deleteDoc(doc(db, 'totemFollows', totemFollowId(userId, groupId, totemId)))
        track('totem_unfollowed', { group_id: groupId })
      })
    },
    [userId],
  )

  return { follows, followedRefs, followedKeys, isFollowing, followTotem, unfollowTotem, loading, error }
}

// Creator-facing: who follows a given totem. Separate from the follower-facing
// hook above because it queries by totem rather than by user, and only the
// group creator has a reason to run it.
export function useTotemFollowers(groupId: string | null, totemId: string | null) {
  const ref =
    groupId && totemId
      ? (query(
          collection(db, 'totemFollows'),
          where('groupId', '==', groupId),
          where('totemId', '==', totemId),
        ) as Query<TotemFollow>)
      : null
  const { data: followers, loading, error } = useFirestoreCollection<TotemFollow>(ref)

  const removeFollower = useCallback(
    async (follow: TotemFollow) => {
      await runMutation(fbt("Couldn't remove follower", 'Error toast when removing a follower from a totem fails'), async () => {
        await deleteDoc(doc(db, 'totemFollows', follow.id))
      })
    },
    [],
  )

  return { followers, removeFollower, loading, error }
}
